/**
 * RevolutionSelectbox — custom dropdown/selectbox widget.
 * <localization-selectors> — custom element wrapping market/language selectors.
 *
 * Extracted from global.js and loaded globally by `snippets/theme-scripts.liquid`.
 * Exposes `window.RevolutionSelectbox` as a backwards-compatibility bridge.
 *
 * Dependencies:
 *   - @theme/delegate, @theme/responsive, @theme/body-scroll-lock-options
 *   - @theme/focus-trap
 *   - @theme/body-scroll-lock
 *
 * Localization market-order contract:
 *   - <localization-modal data-market-order="handle, Display label">
 *   - option rows expose data-market-handle and data-market-name
 */

import { Delegate } from '@theme/delegate';
import { responsive } from '@theme/responsive';
import { bodyScrollLockOptions } from '@theme/body-scroll-lock-options';
import { trapFocus, removeTrapFocus } from '@theme/focus-trap';
import { disableBodyScroll, clearAllBodyScrollLocks } from '@theme/body-scroll-lock';

export class RevolutionSelectbox {
  constructor(element, popover, additionalOverlay) {
    this.element = element;
    this.popover = popover;
    this.changeOverlay = additionalOverlay;
    this.domDelegate = new Delegate(this.element);
    this.delegateBody = new Delegate(document.body);
    this.dropdown = this.element.querySelector('.RevolutionSelectbox');
    this.isOpen = false;
    this.mainDropdown = this.element.querySelector('.RevolutionSelectbox--Content');
    this.mainDropdownContent = this.element.querySelector('.RevolutionSelectbox--InnerContent');
    this.mainButton = this.element.querySelector('.RevolutionSelectbox--Button');

    this.connectedSelect = this.element.getAttribute('data-select');
    this.selectbox = this.element.querySelector(`[name=${this.connectedSelect}]`);
    if (this.selectbox === null) {
      this.selectbox = this.element.querySelector(`[name2=${this.connectedSelect}]`);
    }

    if (this.popover) {
      this.mainDropdown.classList.remove('RevolutionSelectbox--animation');
      this.mainDropdown.classList.add('PopoverContent');
      if (this.changeOverlay) {
        this.pageOverlayElement = document.querySelector('.localizationOverlay');
      } else {
        this.pageOverlayElement = document.querySelector('.page-overlay');
      }
    }

    this.bindEvents();
    this.initSelectbox();
  }

  bindEvents() {
    this.onClickOutsideListener = this.onClickOutside.bind(this);
    this.domDelegate.on('click', '[data-action=toggle-selectbox]', this.toggleDropdown.bind(this));
    this.domDelegate.on('click', '[data-action=selectbox-submit]', this.selectboxSubmit.bind(this));
    this.domDelegate.on(
      'keypress',
      '[data-action=selectbox-submit]',
      this.selectboxSubmit.bind(this)
    );
    this.domDelegate.on('click', '[data-action=selectbox-change]', this.selectboxChange.bind(this));

    this.delegateBody.on('menu:open', this.onClickOutside.bind(this));
    this.domDelegate.on('click', 'body', this.onClickOutside.bind(this));
  }

  selectboxChange(event) {
    const value = event.target.getAttribute('data-value');
    const selectboxID = event.target.getAttribute('data-selectboxid');
    const innerContent = event.target.innerHTML;
    const selEl = this.element.querySelector(`#${selectboxID}`);
    if (selEl.nodeName === 'SELECT') {
      selEl.value = value;
      selEl.dispatchEvent(new Event('change'));
    } else {
      selEl.querySelector(`[value="${value}"]`).setAttribute('checked', 'true');
    }
    this.mainButton.querySelector('.RevolutionSelectbox--Title').innerHTML = innerContent;

    if (this.element.classList.contains('dropdownSelector')) {
      this.element
        .querySelector('.RevolutionSelectbox--Item[disabled="true"]')
        .removeAttribute('disabled');
      event.target.setAttribute('disabled', 'true');
    }
    this.closeDropdown(this.element);
  }

  onClickOutside(event) {
    const withinBoundaries = event.composedPath().includes(this.element);
    if (!withinBoundaries || event.target.classList.contains('FiltersDesktopWrapper')) {
      this.closeDropdown(this.element);
    }
  }

  selectboxSubmit(event) {
    if (event.key !== 'Enter' && event.key !== undefined) {
      return;
    }
    const value = event.target.getAttribute('data-value');
    const dataName = event.target.getAttribute('data-name');
    this.selectbox.value = value;
    this.closeDropdown(this.element);
    this.mainButton.querySelector('.RevolutionSelectbox--Title').innerHTML = dataName;
    this.selectbox.parentElement.submit();
  }

  initSelectbox() {
    const selectedItem = this.mainDropdown.querySelector('[selected="selected"]');
    const titleElement = this.element.querySelector('.RevolutionSelectbox--Title');
    if (selectedItem) {
      titleElement.innerHTML = selectedItem.getAttribute('data-name');
    }
    trapFocus(this.element);
  }

  toggleDropdown(event, target) {
    event.preventDefault();
    if (this.isOpen) {
      this.closeDropdown(target);
    } else {
      this.openDropdown(target);
    }
  }

  openDropdown(target) {
    this.element.dispatchEvent(new CustomEvent('close:search', { bubbles: true }));
    this.element.dispatchEvent(new CustomEvent('menu:close', { bubbles: true }));

    const siblings = Array.from(target.parentElement.parentElement.children).filter((item) =>
      item.classList.contains('RevolutionSelectbox')
    );

    siblings.forEach((item) => {
      item.querySelector('.RevolutionSelectbox--Button').setAttribute('aria-expanded', false);
      item.querySelector('.RevolutionSelectbox--Content').setAttribute('aria-hidden', true);
      item.querySelector('.RevolutionSelectbox--Content').style.display = 'none';
    });

    if (this.popover) {
      this.pageOverlayElement.classList.add('is-visible');
      this.pageOverlayElement.addEventListener('click', this.onClickOutsideListener);
      const focus = this.element.querySelector('[data-scrollable]');
      if (typeof bodyScrollLock !== 'undefined') {
        disableBodyScroll(focus, bodyScrollLockOptions);
      }
      document.body.classList.add('scrollLock');
      trapFocus(this.mainDropdown);
      const drawer = document.querySelector('#Drawer');
      if (drawer && drawer.getAttribute('aria-expanded') === 'true') {
        document.querySelector('.Drawer--Wrapper').style.overflow = 'visible';
      }
    }

    const contentElement = target.parentElement.querySelector('.RevolutionSelectbox--Content');
    target.setAttribute('aria-expanded', 'true');
    contentElement.style.display = 'block';
    contentElement.style.height = `${contentElement.offsetHeight}px`;

    if (this.popover) {
      setTimeout(() => {
        contentElement.setAttribute('aria-hidden', 'false');
      }, 20);
    } else {
      contentElement.setAttribute('aria-hidden', 'false');
    }

    this.isOpen = true;
    window.addEventListener('click', this.onClickOutsideListener);
  }

  closeDropdown(target) {
    let closestDropdown = target.closest('.RevolutionSelectbox');
    if (closestDropdown === null) {
      closestDropdown = target.querySelector("[aria-expanded='true']").parentElement;
    }
    closestDropdown
      .querySelector('.RevolutionSelectbox--Button')
      .setAttribute('aria-expanded', false);
    closestDropdown
      .querySelector('.RevolutionSelectbox--Content')
      .setAttribute('aria-hidden', true);
    setTimeout(() => {
      closestDropdown.querySelector('.RevolutionSelectbox--Content').style.display = 'none';
    }, 300);

    if (this.popover) {
      this.pageOverlayElement.classList.remove('is-visible');
      this.pageOverlayElement.removeEventListener('click', this.onClickOutsideListener);

      const menuDrawer = document.querySelector('#Drawer');
      if (menuDrawer && menuDrawer.getAttribute('aria-expanded') === 'true') {
        document.querySelector('.Drawer--Wrapper').style.overflow = 'auto';
      } else if (typeof bodyScrollLock !== 'undefined') {
        clearAllBodyScrollLocks();
        document.body.classList.remove('scrollLock');
      }
    }

    this.isOpen = false;
    removeTrapFocus(this.mainDropdownContent);
    window.removeEventListener('click', this.onClickOutsideListener);
  }
}

// Backwards-compatibility bridge for integrations that still read the global.
window.RevolutionSelectbox = RevolutionSelectbox;



/**
 * Wraps a localization trigger button (market or language). On desktop it
 * anchors the trigger's popup (#modal-market / #modal-language) horizontally
 * under the button, so the popup opens directly beneath the header selector.
 *
 * Open/close, overlay (outside) click and ESC are handled by popup.js; this
 * element only listens for the bubbling `popup:opened` / `popup:closed` events
 * to set/clear the popup's horizontal inset. Mobile drawer triggers
 * (data-action="open-drawer") and footer triggers are left untouched.
 */
class LocalizationSelectors extends HTMLElement {
  connectedCallback() {
    if (this._mounted) return;
    this._mounted = true;

    this.trigger = this.querySelector('.localization-sel__btn[aria-controls]');
    this.targetId = this.trigger?.getAttribute('aria-controls') || null;
    // Only desktop popups get anchored; mobile drawers use data-action="open-drawer".
    this._isPopupTrigger = this.trigger?.getAttribute('data-action') === 'toggle-popup';
    if (!this._isPopupTrigger || !this.targetId) return;

    // before-open: pre-position before the popup paints (avoids a one-frame flash
    // at the CSS default inset, since the desktop popup has no opacity transition).
    // opened: re-correct once body-scroll-lock has settled layout (scrollbar gutter).
    this._onPopupOpening = this._handlePopupOpening.bind(this);
    this._onPopupClosed = this._handlePopupClosed.bind(this);

    document.addEventListener('popup:before-open', this._onPopupOpening);
    document.addEventListener('popup:opened', this._onPopupOpening);
    document.addEventListener('popup:closed', this._onPopupClosed);
  }

  disconnectedCallback() {
    if (!this._mounted) return;
    this._mounted = false;

    document.removeEventListener('popup:before-open', this._onPopupOpening);
    document.removeEventListener('popup:opened', this._onPopupOpening);
    document.removeEventListener('popup:closed', this._onPopupClosed);
    this._unbindReposition();
    this._popup = null;
  }

  _handlePopupOpening(event) {
    const popup = event.target;
    if (!popup || popup.id !== this.targetId) return;
    if (!responsive.matchesBreakpoint('lap_and_up') || !this._isPrimaryTrigger()) return;

    this._popup = popup;
    this._bindReposition();
    this._reposition();
  }

  _bindReposition() {
    // Idempotent: both popup:before-open AND popup:opened reach here for one open.
    // Without this guard the second call overwrites _resizeHandle, orphaning the
    // first resize listener (close only removes the newest one) → a leak per open.
    if (this._resizeHandle) return;
    // Shared resize multiplexer instead of a raw window listener — keeps every
    // resize-driven module on one ticker (see assets/responsive.js).
    this._resizeHandle = responsive.addResizeListener(this, this._reposition);
  }

  _handlePopupClosed(event) {
    if (event.target?.id !== this.targetId) return;
    this._unbindReposition();
    if (this._popup) {
      this._popup.style.removeProperty('right');
      this._popup.style.removeProperty('left');
      this._popup = null;
    }
  }

  /**
   * The same popup id can have several triggers (header + footer). Only the
   * top-most visible one (the header) should drive the fixed-top popup, so
   * footer triggers don't fight over its horizontal position.
   */
  _isPrimaryTrigger() {
    const triggers = Array.from(
      document.querySelectorAll(
        `.localization-sel__btn[data-action="toggle-popup"][aria-controls="${this.targetId}"]`
      )
    ).filter((el) => el.offsetParent !== null);
    if (triggers.length <= 1) return triggers[0] === this.trigger;

    const primary = triggers.reduce((top, el) =>
      el.getBoundingClientRect().top < top.getBoundingClientRect().top ? el : top
    );
    return primary === this.trigger;
  }

  _reposition() {
    if (!this._popup || !responsive.matchesBreakpoint('lap_and_up')) return;
    const rect = this.trigger.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const margin = 8;
    // Align the popup's left edge with the trigger's left edge (popup opens rightward).
    // Clamp so it never overflows the right viewport edge (width known only once shown).
    let left = Math.round(rect.left);
    const popupWidth = this._popup.offsetWidth;
    if (popupWidth) {
      left = Math.min(left, viewportWidth - popupWidth - margin);
    }
    left = Math.max(margin, left);
    this._popup.style.setProperty('left', `${left}px`, 'important');
    this._popup.style.setProperty('right', 'auto', 'important');
  }

  _unbindReposition() {
    this._resizeHandle?.removeResizeListener();
    this._resizeHandle = null;
  }
}
if (!customElements.get('localization-selectors')) {
  customElements.define('localization-selectors', LocalizationSelectors);
}

class LocalizationModal extends HTMLElement {
  constructor() {
    super();
    this.element = this;
    this.enableDropdownAnimations = window.theme
      ? window.theme.enableDropdownAnimations !== false
      : true;
  }

  connectedCallback() {
    // Idempotent: a DOM move fires disconnected→connected; on some platforms (and
    // Shopify section reloads) connected can fire twice — without this guard we'd
    // double-bind every listener and create a second MutationObserver.
    if (this._mounted) return;
    this._mounted = true;

    this.delegateElement = new Delegate(this.element);
    // NOTE: do not instantiate RevolutionSelectbox here. The localization list is a
    // flat `.RevolutionSelectbox--Selectors` (hidden input + items, no `--Content`/
    // toggle button), which the generic widget can't parse — it threw in initSelectbox
    // and its selectbox-change handler crashed on the hidden input. LocalizationModal
    // owns selection via handleLocalizationSelectboxClick → syncSelectboxUI / checkLocationSubmit.
    this.bindEvents();
    this.ensureLocalizationData();
    if (this.ensureLanguageSelection) this.ensureLanguageSelection();
    this.sortMarketItems();
    this.initRovingTabindex();
    this.enableTouchScrollGuard();
    this.observeSelectboxExpandedState();

    this.element.querySelectorAll('.RevolutionSelectbox--Selectors').forEach((box) => {
      const btn = box.querySelector('.RevolutionSelectbox--Button');
      const panel = box.querySelector('.RevolutionSelectbox--Content');
      const count = box.querySelectorAll('.RevolutionSelectbox--Item').length;
      if (!btn || !panel) return;
      if (count <= 1) {
        btn.classList.add('rs-disabled');
        btn.setAttribute('disabled', 'disabled');
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('aria-expanded', 'false');
        panel.setAttribute('aria-hidden', 'true');
        panel.style.display = 'none';
      } else {
        btn.classList.remove('rs-disabled');
        btn.removeAttribute('disabled');
        btn.setAttribute('aria-disabled', 'false');
      }
    });
  }

  bindEvents() {
    this._onDropdownOpened = this.checkFadeVisibility.bind(this);
    document.addEventListener('dropdown:opened', this._onDropdownOpened);
    document.body.addEventListener('selectbox:opened', this._onDropdownOpened);

    this._onCountryChange = this.updateLanguageOptions.bind(this);
    document.addEventListener('change:countrySelector', this._onCountryChange);

    // Shared resize multiplexer instead of a raw window listener — keeps every
    // resize-driven module on one ticker (see assets/responsive.js).
    this._resizeHandle = responsive.addResizeListener(this, this.checkFadeVisibility);

    this._onLocalizationSelectboxClick = this.handleLocalizationSelectboxClick.bind(this);
    this.element.addEventListener('click', this._onLocalizationSelectboxClick, true);

    this.delegateElement.on(
      'click',
      '[data-action=check-location-submit]',
      this.checkLocationSubmit.bind(this)
    );
    this.delegateElement.on('click', '.RevolutionSelectbox--Item', (e, target) => {
      const box = target.closest('.RevolutionSelectbox');
      if (!box) return;
      if (target.closest('.rs-country__selectbox')) {
        this.handleRegularMarketSelection(e, target);
      } else {
        this.syncSelectboxUI(box, target);
        requestAnimationFrame(() => {
          this.closeSelectbox(box);
          this.checkLocationSubmit(null, target);
        });
      }
    });
    this.delegateElement.on('keydown', '.RevolutionSelectbox--Item', (e, target) => {
      // Listbox keyboard contract: Enter/Space selects; Arrow/Home/End move the
      // active option via roving tabindex (only one option is tabbable at a time,
      // matching a native <select>). Escape is handled by the popup/drawer host.
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        target.click();
        return;
      }
      if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) {
        const box = target.closest('.RevolutionSelectbox');
        if (!box) return;
        const items = this._optionItems(box);
        const idx = items.indexOf(target);
        if (idx === -1) return;
        e.preventDefault();
        let next;
        if (e.key === 'ArrowDown') next = items[Math.min(items.length - 1, idx + 1)];
        else if (e.key === 'ArrowUp') next = items[Math.max(0, idx - 1)];
        else if (e.key === 'Home') next = items[0];
        else next = items[items.length - 1];
        this._setActiveOption(box, next);
      }
    });
    this.delegateElement.on('click', '.RevolutionSelectbox--Button', () => {
      // Wait for selectbox open/close DOM updates, then reuse existing fade-visible logic.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.checkFadeVisibility());
      });
    });
    this._panelScrollHandlers = [];
    this.element.querySelectorAll('.RevolutionSelectbox--Content').forEach((panel) => {
      const scrollEl = this._getScrollEl(panel);
      const fn = this.checkFadeVisibility.bind(this);
      this._panelScrollHandlers.push({ panel: scrollEl, fn });
      scrollEl.addEventListener('scroll', fn);
    });
  }

  disconnectedCallback() {
    if (!this._mounted) return;
    this._mounted = false;

    if (this._onDropdownOpened) {
      document.removeEventListener('dropdown:opened', this._onDropdownOpened);
      document.body.removeEventListener('selectbox:opened', this._onDropdownOpened);
    }
    if (this._onCountryChange) document.removeEventListener('change:countrySelector', this._onCountryChange);
    this._resizeHandle?.removeResizeListener();
    if (this._onLocalizationSelectboxClick) {
      this.element.removeEventListener('click', this._onLocalizationSelectboxClick, true);
    }
    if (this._panelScrollHandlers) {
      this._panelScrollHandlers.forEach(({ panel, fn }) => panel.removeEventListener('scroll', fn));
    }
    if (this._touchScrollHandlers) {
      this._touchScrollHandlers.forEach(({ el, startFn, moveFn }) => {
        el.removeEventListener('touchstart', startFn);
        el.removeEventListener('touchmove', moveFn);
      });
    }
    this._onDropdownOpened = null;
    this._onCountryChange = null;
    this._resizeHandle = null;
    this._onLocalizationSelectboxClick = null;
    this._panelScrollHandlers = null;
    this._touchScrollHandlers = null;
    if (this._selectboxExpandedObserver) {
      this._selectboxExpandedObserver.disconnect();
      this._selectboxExpandedObserver = null;
    }
  }

  observeSelectboxExpandedState() {
    this._selectboxExpandedObserver?.disconnect?.();
    this._selectboxExpandedObserver = new MutationObserver(() => {
      this.syncLocalizationPopupExpanded();
    });

    this.element.querySelectorAll('.RevolutionSelectbox--Button').forEach((button) => {
      this._selectboxExpandedObserver.observe(button, {
        attributes: true,
        attributeFilter: ['aria-expanded'],
      });
    });

    this.syncLocalizationPopupExpanded();
  }

  enableTouchScrollGuard() {
    this._touchScrollHandlers = [];
    this.element
      .querySelectorAll('.RevolutionSelectbox--Selectors .RevolutionSelectbox--Content-scroll')
      .forEach((el) => {
        let startY = 0;

        const startFn = (event) => {
          startY = event.touches?.[0]?.clientY || 0;
        };

        const moveFn = (event) => {
          // In the mobile drawer this panel is made passive (overflow: visible,
          // max-height: none) so `.Drawer__body` is the real scroller. A non-
          // scrolling element reads as at-top AND at-bottom at once, so the guard
          // below would preventDefault every move and freeze the drawer scroll.
          // Only guard when this panel actually scrolls (the desktop popup case).
          const isScrollable = el.scrollHeight > el.clientHeight + 1;
          if (!isScrollable) return;

          const currentY = event.touches?.[0]?.clientY || 0;
          const deltaY = startY - currentY;
          const atTop = el.scrollTop <= 0;
          const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;

          // Keep touch scrolling inside dropdown on iOS instead of bubbling to popup/page.
          if ((atTop && deltaY < 0) || (atBottom && deltaY > 0)) {
            event.preventDefault();
          }
          event.stopPropagation();
        };

        el.addEventListener('touchstart', startFn, { passive: true });
        el.addEventListener('touchmove', moveFn, { passive: false });
        this._touchScrollHandlers.push({ el, startFn, moveFn });
      });
  }

  sortMarketItems() {
    const inner = this.element.querySelector(
      '.rs-country__selectbox .RevolutionSelectbox--InnerContent'
    );
    const rawOrder = this.element.getAttribute('data-market-order') || '';
    if (!inner || !rawOrder.trim()) return;

    const normalize = (value) => value.trim().toLocaleLowerCase();
    const configuredOrder = rawOrder
      .split(/[,;\n]/)
      .map(normalize)
      .filter(Boolean);
    if (!configuredOrder.length) return;

    const orderIndex = new Map(configuredOrder.map((value, index) => [value, index]));
    const items = Array.from(inner.querySelectorAll('.RevolutionSelectbox--Item'));

    const getConfiguredIndex = (item) => {
      const handle = normalize(item.getAttribute('data-market-handle') || '');
      const name = normalize(item.getAttribute('data-market-name') || '');
      return (
        orderIndex.get(handle) ??
        orderIndex.get(name) ??
        Number.MAX_SAFE_INTEGER
      );
    };

    items
      .map((item, sourceIndex) => ({ item, sourceIndex }))
      .sort((a, b) => {
        const orderDifference =
          getConfiguredIndex(a.item) - getConfiguredIndex(b.item);
        return orderDifference || a.sourceIndex - b.sourceIndex;
      })
      .forEach(({ item }) => inner.appendChild(item));
  }

  _getScrollEl(panel) {
    const innerScroll = panel.querySelector('.RevolutionSelectbox--Content-scroll');
    if (!innerScroll) return panel;

    const innerScrollable = innerScroll.scrollHeight > innerScroll.clientHeight + 1;
    if (innerScrollable) return innerScroll;

    return panel;
  }

  handleLocalizationSelectboxClick(event) {
    const button = event.target.closest('.RevolutionSelectbox--Button[data-action="toggle-selectbox"]');
    if (button && this.element.contains(button)) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (
        button.classList.contains('rs-disabled') ||
        button.disabled ||
        button.getAttribute('aria-disabled') === 'true'
      ) {
        this.closeLocalizationSelectbox(button.closest('.RevolutionSelectbox'));
        return;
      }

      this.toggleLocalizationSelectbox(button.closest('.RevolutionSelectbox'));
      return;
    }

    const item = event.target.closest('.RevolutionSelectbox--Item[data-action="selectbox-change"]');
    if (!item || !this.element.contains(item)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const box = item.closest('.RevolutionSelectbox');
    if (!box) return;

    if (item.closest('.rs-country__selectbox')) {
      this.handleRegularMarketSelection(event, item);
      return;
    }

    this.syncSelectboxUI(box, item);
    this.closeSelectbox(box);
    this.checkLocationSubmit(null, item);
  }

  toggleLocalizationSelectbox(box) {
    if (!box) return;

    const button = box.querySelector('.RevolutionSelectbox--Button');
    const isOpen = button?.getAttribute('aria-expanded') === 'true';
    if (isOpen) {
      this.closeLocalizationSelectbox(box);
    } else {
      this.openLocalizationSelectbox(box);
    }
  }

  openLocalizationSelectbox(box) {
    if (!box) return;

    this.setLocalizationPopupExpanded(true);

    this.element.querySelectorAll('.RevolutionSelectbox--Selectors').forEach((otherBox) => {
      if (otherBox !== box) this.closeLocalizationSelectbox(otherBox);
    });

    const button = box.querySelector('.RevolutionSelectbox--Button');
    const panel = box.querySelector('.RevolutionSelectbox--Content');
    if (!button || !panel) return;

    const token = (panel._localizationSelectboxToken || 0) + 1;
    panel._localizationSelectboxToken = token;
    button.setAttribute('aria-expanded', 'true');
    panel.style.display = 'block';
    panel.style.height = '';
    panel.setAttribute('aria-hidden', 'false');

    if (!this.enableDropdownAnimations) {
      panel.style.maxHeight = `${this.getLocalizationPanelHeight(panel)}px`;
      this.checkFadeVisibility();
      return;
    }

    panel.style.maxHeight = '0px';
    requestAnimationFrame(() => {
      if (panel._localizationSelectboxToken !== token) return;
      const maxHeight = this.getLocalizationPanelHeight(panel);
      panel.style.maxHeight = `${maxHeight}px`;
      this.checkFadeVisibility();
    });
  }

  closeLocalizationSelectbox(box) {
    if (!box) return;

    const button = box.querySelector('.RevolutionSelectbox--Button');
    const panel = box.querySelector('.RevolutionSelectbox--Content');
    if (!button || !panel) return;

    const token = (panel._localizationSelectboxToken || 0) + 1;
    panel._localizationSelectboxToken = token;
    button.setAttribute('aria-expanded', 'false');
    panel.style.display = 'block';
    panel.style.height = '';

    if (!this.enableDropdownAnimations) {
      panel.setAttribute('aria-hidden', 'true');
      panel.style.maxHeight = '0px';
      this.syncLocalizationPopupExpanded();
      this.checkFadeVisibility();
      return;
    }

    panel.style.maxHeight = `${panel.offsetHeight || 0}px`;
    requestAnimationFrame(() => {
      if (panel._localizationSelectboxToken !== token) return;
      panel.setAttribute('aria-hidden', 'true');
      panel.style.maxHeight = '0px';
      this.syncLocalizationPopupExpanded();
      this.checkFadeVisibility();
    });
  }

  setLocalizationPopupExpanded(isExpanded) {
    const modal = this.closest('.country-selector');
    if (!modal) return;

    modal.classList.toggle('country-selector--expanded', isExpanded);
    this.positionLocalizationPopup(modal, isExpanded);
    if (isExpanded) {
      modal.scrollTop = 0;
      const body = modal.querySelector('.country-selector__body');
      if (body) body.scrollTop = 0;
    }
  }

  positionLocalizationPopup(modal, isExpanded) {
    if (!modal) return;

    if (!isExpanded) {
      modal.style.removeProperty('top');
      modal.style.removeProperty('max-height');
      return;
    }

    const top = this.getLocalizationExpandedTop();
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const bottomSpace = 8;

    modal.style.top = `${top}px`;
    modal.style.maxHeight = `${Math.max(260, viewportHeight - top - bottomSpace)}px`;
  }

  /**
   * Parses the popup's `--localization-popup-offset-from-chrome` (e.g. 3.125rem) for JS layout fallback.
   * @param {HTMLElement} modal
   * @returns {number}
   */
  #getLocalizationPopupChromeOffsetPx(modal) {
    const rootFont = parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
    const raw = window.getComputedStyle(modal).getPropertyValue('--localization-popup-offset-from-chrome').trim();
    if (raw.endsWith('rem')) {
      return Math.round((parseFloat(raw) || 3.125) * rootFont);
    }
    if (raw.endsWith('px')) {
      const n = parseFloat(raw);
      return Number.isFinite(n) ? Math.round(n) : Math.round(3.125 * rootFont);
    }
    return Math.round(3.125 * rootFont);
  }

  getLocalizationExpandedTop() {
    const modal = this.closest('.country-selector');
    const rootStyles = window.getComputedStyle(document.documentElement);
    const parsePx = (value) => {
      const n = parseFloat(String(value || '').replace('px', '').trim());
      return Number.isFinite(n) ? n : 0;
    };

    // Open exactly under the top bar: prefer the real header height (matches `.localizationOverlay`),
    // fall back to the `--localization-popup-offset-from-chrome` token when no header is present.
    const headerHeight = parsePx(rootStyles.getPropertyValue('--header-height'));
    const offsetPx = headerHeight > 0
      ? headerHeight
      : (modal ? this.#getLocalizationPopupChromeOffsetPx(modal) : Math.round(3.125 * 16));

    return Math.max(
      0,
      Math.round(parsePx(rootStyles.getPropertyValue('--announcement-dynamic-height')) + offsetPx)
    );
  }

  syncLocalizationPopupExpanded() {
    const hasOpenSelectbox = Array.from(
      this.element.querySelectorAll('.RevolutionSelectbox--Button')
    ).some((button) => button.getAttribute('aria-expanded') === 'true');

    this.setLocalizationPopupExpanded(hasOpenSelectbox);
  }

  getLocalizationPanelHeight(panel) {
    const capPx = this.getLocalizationPanelMaxHeight(panel);
    if (panel.closest('localization-modal')) {
      return capPx;
    }
    const scrollEl = panel.querySelector('.RevolutionSelectbox--Content-scroll');
    const contentHeight = scrollEl ? scrollEl.scrollHeight : panel.scrollHeight;
    const cssMaxHeight = parseFloat(window.getComputedStyle(panel).maxHeight);
    const maxHeight = Number.isFinite(cssMaxHeight) && cssMaxHeight > 0 ? cssMaxHeight : capPx;
    return Math.min(contentHeight, maxHeight);
  }

  getLocalizationPanelMaxHeight(panel) {
    const modal = panel.closest('.country-selector');
    const raw = window.getComputedStyle(modal || panel)
      .getPropertyValue('--localization-selector-panel-max-height')
      .trim();

    if (raw.endsWith('rem')) {
      const rootFontSize = parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
      return (parseFloat(raw) || 13.75) * rootFontSize;
    }

    if (raw.endsWith('px')) return parseFloat(raw) || 213;

    return 213;
  }

  closeSelectbox(box) {
    if (!box) return;

    if (this.element.contains(box)) {
      this.closeLocalizationSelectbox(box);
      return;
    }

    const btn = box.querySelector('.RevolutionSelectbox--Button');
    const panel = box.querySelector('.RevolutionSelectbox--Content');
    if (btn && panel) {
      btn.setAttribute('aria-expanded', 'false');
      panel.setAttribute('aria-hidden', 'true');
      panel.style.display = 'none';
    }
  }

  checkFadeVisibility() {
    const panels = Array.from(this.element.querySelectorAll('.RevolutionSelectbox--Content'));
    panels.forEach((panel) => {
      const isOpen =
        panel.getAttribute('aria-hidden') === 'false' &&
        getComputedStyle(panel).display !== 'none';
      if (!isOpen) {
        panel.classList.remove('fade-visible');
        return;
      }
      const scrollEl = this._getScrollEl(panel);
      const isScrollable = scrollEl.scrollHeight > scrollEl.clientHeight + 1;
      const distanceFromBottom =
        scrollEl.scrollHeight - (scrollEl.scrollTop + scrollEl.clientHeight);
      const isAtBottom = distanceFromBottom <= 2;
      panel.classList.toggle('fade-visible', isScrollable && !isAtBottom);
    });
  }

  escapeHtml(value) {
    const span = document.createElement('span');
    span.textContent = value || '';
    return span.innerHTML;
  }

  getMarketName(item) {
    const marketName = (item.getAttribute('data-market-name') || '').trim();
    if (marketName) return marketName;

    const name = (item.getAttribute('data-name') || '').trim();
    return name.split('/')[0].trim() || name;
  }

  updateMarketDescription(item) {
    const description = this.element.querySelector('#localization-modal-desc');
    const template = this.element.getAttribute('data-description-template');
    if (!description || !template || !item) return;

    const market = this.getMarketName(item);
    const currency = (item.getAttribute('data-currency') || '').trim();
    if (!market || !currency) return;

    description.innerHTML = template
      .split('%%MARKET%%').join(this.escapeHtml(market))
      .split('%%CURRENCY%%').join(this.escapeHtml(currency));
  }


  _optionItems(box) {
    return Array.from(box.querySelectorAll('.RevolutionSelectbox--Item'));
  }

  // Roving tabindex: the given option becomes the single tab stop and takes focus.
  _setActiveOption(box, item) {
    if (!item) return;
    this._optionItems(box).forEach((el) => {
      el.setAttribute('tabindex', el === item ? '0' : '-1');
    });
    item.focus();
  }

  // Guarantee each listbox has exactly one tabbable option even if no option is
  // pre-selected (current market/language not in the list). Markup already marks
  // the selected one; this is the fallback.
  initRovingTabindex() {
    this.element
      .querySelectorAll('.RevolutionSelectbox--InnerContent[role="listbox"]')
      .forEach((listbox) => {
        const items = Array.from(listbox.querySelectorAll('.RevolutionSelectbox--Item'));
        if (!items.length) return;
        if (items.filter((el) => el.getAttribute('tabindex') === '0').length === 1) return;
        const active =
          items.find((el) => el.getAttribute('aria-selected') === 'true') || items[0];
        items.forEach((el) => el.setAttribute('tabindex', el === active ? '0' : '-1'));
      });
  }

  syncSelectboxUI(box, item) {
    const value = (item.getAttribute('data-value') || '').trim();
    const name = (item.getAttribute('data-name') || '').trim();
    const inputId = (item.getAttribute('data-selectboxid') || '').trim();

    if (inputId && value) {
      // The same localization snippet is rendered twice (desktop popup + mobile
      // drawer), so this id is NOT unique in the document. document.getElementById
      // returns the FIRST match — which on mobile can be the desktop instance's
      // hidden input, making the form submit a stale value while the UI looks
      // correct. Scope the lookup to the selectbox the user is interacting with
      // (the hidden input is a direct child of `box`), then to this modal instance.
      const hidden =
        box.querySelector(`#${CSS.escape(inputId)}`) ||
        this.element.querySelector(`#${CSS.escape(inputId)}`);
      if (hidden) hidden.value = value;
    }

    const titleEl = box.querySelector('.RevolutionSelectbox--Button .RevolutionSelectbox--Title');
    if (titleEl && name) titleEl.textContent = name;

    // Keep selected market flag visible in the button label.
    if (box.classList.contains('rs-country__selectbox')) {
      const selectedFlag = item.querySelector('.revolution-selectbox__flag');
      const buttonFlag = box.querySelector('.RevolutionSelectbox--Button .revolution-selectbox__flag');
      if (buttonFlag && selectedFlag) {
        buttonFlag.innerHTML = selectedFlag.innerHTML;
      }
    }

    box.querySelectorAll('.RevolutionSelectbox--Item[aria-selected="true"]').forEach((el) => {
      if (el !== item) el.setAttribute('aria-selected', 'false');
    });
    item.setAttribute('aria-selected', 'true');

  }


  /**
   * Submits the row the visitor actually selected and synchronizes only the matching
   * localization preference. Market and language forms both carry country and language
   * inputs, so the clicked row's selectbox determines which preference changed.
   */
  async checkLocationSubmit(event, chosenItem) {
    if (event?.preventDefault) event.preventDefault();
    try {
      const form = this.element.closest('form');
      if (!form) {
        console.error('[Localization] localization form not found');
        return;
      }

      const selectedItem =
        chosenItem ||
        this.element.querySelector(
          '.rs-country__selectbox .RevolutionSelectbox--Item[aria-selected="true"]'
        ) ||
        this.element.querySelector(
          '.RevolutionSelectbox--Item[aria-selected="true"]'
        ) ||
        this.element.querySelector('.RevolutionSelectbox--Item.selected');
      if (!selectedItem) {
        console.warn('[Localization] No selected item found.');
        return;
      }

      const selectedValue = (selectedItem.getAttribute('data-value') || '').trim();
      if (!selectedValue) {
        console.warn('[Localization] Selected item has empty data-value.');
        return;
      }

      const isCountryChange = Boolean(selectedItem.closest('.rs-country__selectbox'));
      const isLanguageChange = Boolean(selectedItem.closest('.rs-language__selectbox'));
      if (!isCountryChange && !isLanguageChange) {
        console.warn('[Localization] Selected item is not a country or language option.');
        return;
      }

      if (isCountryChange) {
        const countryCode = selectedValue.toUpperCase();
        const countryInput = form.querySelector('input[name="country_code"]');
        if (countryInput) countryInput.value = countryCode;

        this.alignLanguageToMarket(selectedItem);
        this.alignReturnToMarket(countryCode);
        this.alignRootLocalizationCookie(countryCode);
        this.syncOrbeCountryPreference(countryCode);
      } else {
        const languageInput = form.querySelector('input[name="language_code"]');
        if (languageInput) languageInput.value = selectedValue;

        this.alignReturnToMarket();
        this.syncOrbeLanguagePreference(selectedValue);
      }

      if (this.ensureLanguageSelection) this.ensureLanguageSelection();
      const hiddenLang = this.element.querySelector('#selected-language');
      if (hiddenLang) {
        // If no radio selected yet, select first visible one
        if (!hiddenLang.value) {
          const firstRadio = this.element.querySelector(
            '.localization__language-radio'
          );
          if (firstRadio) {
            firstRadio.checked = true;
            hiddenLang.value = firstRadio.value;
          }
        }
      }

      form.submit();
    } catch (error) {
      console.error('Localization submit error:', error);
    }
  }

  handleRegularMarketSelection(e, target) {
    if (!target.closest('.rs-country__selectbox')) return;

    this.element
      .querySelectorAll(
        '.rs-country__selectbox .RevolutionSelectbox--Item[aria-selected="true"]'
      )
      .forEach((item) => {
        if (item !== target) item.setAttribute('aria-selected', 'false');
      });
    target.setAttribute('aria-selected', 'true');

    const value = (target.getAttribute('data-value') || '').trim();
    const box = target.closest('.RevolutionSelectbox');

    const countryInput = this.element.querySelector('#country-selector__country-code');
    if (countryInput && value) countryInput.value = value;

    if (box) this.syncSelectboxUI(box, target);
    if (box) this.closeSelectbox(box);

    // Market and language are independent selectors: picking a market switches to it
    // (reload — Shopify has no client-side market switch), no auto-open of the language
    // selector. The language list reflects the active market after reload; language is
    // changed separately in its own selector.
    this.checkLocationSubmit(null, target);
  }

  /**
   * Shopify remembers the chosen country in a `localization` cookie, and the one written on a
   * localized storefront is scoped to that market's path - `/en-eu`. The browser then sends it
   * only under that path, while `/` and `/sv-se` keep sending whatever the root-scoped cookie
   * says. A visitor who had been on the Swedish store once carried `localization=SE` at `/`
   * for a year, so every route outside `/en-eu` put them back on the Swedish market however
   * correctly the switch itself had gone.
   *
   * Writing the choice at the root as well leaves every path agreeing on it. Shopify's own
   * cookie is a plain year-long Lax cookie, and this matches it rather than inventing a
   * second scope.
   */
  alignRootLocalizationCookie(countryCode) {
    const code = (countryCode || '').trim().toUpperCase();
    if (!code) return;

    const oneYearInSeconds = 60 * 60 * 24 * 365;
    document.cookie = `localization=${code}; path=/; max-age=${oneYearInSeconds}; SameSite=Lax`;
  }

  /**
   * Orbe does not see Shopify's native localization form submit. Keep its manual country
   * preference aligned with the market row the visitor picked so IP geolocation does not
   * immediately redirect them back.
   */
  syncOrbeCountryPreference(countryCode) {
    const code = (countryCode || '').trim().toUpperCase();
    if (!code) return;

    this.writeOrbePreferenceCookie('mdApp_countryCodeDomain', code);
  }

  /**
   * A language click is a separate preference in Orbe and must not rewrite its country
   * preference merely because Shopify includes both hidden inputs in the form.
   */
  syncOrbeLanguagePreference(languageCode) {
    const code = (languageCode || '').trim();
    if (!code) return;

    this.writeOrbePreferenceCookie('mdApp_showRecommendationLang', code);
  }

  /**
   * Orbe's runtime cookie on this single-host, path-based Markets store is host-only. Keeping
   * the same scope replaces that cookie; adding `Domain` creates a second cookie with the same
   * name and leaves the app free to read the stale one.
   *
   * Ref: https://help.orbe.app/developer-reference/integrate-your-selector
   */
  writeOrbePreferenceCookie(name, value) {
    const sixtyDaysInSeconds = 60 * 60 * 24 * 60;
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${sixtyDaysInSeconds}; SameSite=Lax`;
  }

  /**
   * Shopify's localization form ships a hidden `return_to` holding the current path, and the
   * locale prefix in that path is what decides the market. Coming from the Swedish storefront
   * it reads `/sv-se?...`, so the visitor was sent straight back to SE however correct the
   * country and language in the same submit were - the language fix alone did not help.
   *
   * Stripping the prefix leaves a path the new market applies its own to, so the visitor
   * stays on the page they were reading, in the market they picked. For a market change,
   * Orbe also requires the selected country in the URL so the choice survives a domain or
   * market redirect.
   */
  alignReturnToMarket(countryCode = '') {
    const form = this.element.closest('form');
    const returnToInput = form?.querySelector('input[name="return_to"]');
    if (!returnToInput) return;

    const localeRoot = (window.routes?.localeRootUrl || '').replace(/\/+$/, '');
    let path = window.location.pathname;
    // Only a whole segment counts: a bare startsWith would turn /en-search into /arch when
    // the locale root happens to be /en-se.
    if (localeRoot && (path === localeRoot || path.startsWith(`${localeRoot}/`))) {
      path = path.slice(localeRoot.length) || '/';
    }

    const returnToUrl = new URL(
      `${path}${window.location.search}${window.location.hash}`,
      window.location.origin
    );
    const code = (countryCode || '').trim().toUpperCase();
    if (code) returnToUrl.searchParams.set('country', code);

    returnToInput.value = `${returnToUrl.pathname}${returnToUrl.search}${returnToUrl.hash}`;
  }

  /**
   * The market form carries the language the page was rendered in, and Shopify serves the
   * pair - so asking for a market that does not publish that language sends the visitor to
   * one that does. Browsing in Swedish and picking EU landed back on the SE storefront,
   * while the same click from English worked, which is why it looked like a browser bug.
   *
   * Each market row lists the languages it publishes (data-languages), so the language is
   * only kept when the market has it and otherwise falls to that market's first one.
   */
  alignLanguageToMarket(selectedItem) {
    const form = this.element.closest('form');
    const languageInput = form?.querySelector('input[name="language_code"]');
    if (!languageInput) return;

    let languages = [];
    try {
      languages = JSON.parse(selectedItem.getAttribute('data-languages') || '[]');
    } catch {
      return; // Nothing to check it against - leave the form as Liquid rendered it.
    }
    if (!languages.length) return;

    const current = (languageInput.value || '').toLowerCase();
    const supported = languages.some((language) => (language.iso || '').toLowerCase() === current);
    if (supported) return;

    languageInput.value = languages[0].iso;
  }

  ensureLanguageSelection() {
    const hidden = this.element.querySelector('#selected-language');
    const radios = Array.from(
      this.element.querySelectorAll('.language-selector__radio')
    );
    if (!radios.length) return;
    let checked = radios.find((r) => r.checked);
    if (!checked && hidden && hidden.value) {
      checked = radios.find(
        (r) => r.value.toLowerCase() === hidden.value.toLowerCase()
      );
    }
    if (!checked) checked = radios[0];
    if (checked && !checked.checked) checked.checked = true;
    if (hidden && checked && hidden.value !== checked.value)
      hidden.value = checked.value;
  }

  async ensureLocalizationData() {
    if (this._localizationData) return this._localizationData;
    if (!this._localizationDataPromise) this._localizationDataPromise = Promise.resolve(null);
    return this._localizationDataPromise;
  }

  parseLanguages(raw) {
    if (!raw) return [];

    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((l) => ({
          iso: (l.iso || l.code || '').toLowerCase(),
          name: l.name || l.endonym_name || (l.iso || '').toUpperCase(),
        }))
        .filter((l) => l.iso);
    } catch {
      return [];
    }
  }

  async fallbackLanguagesFromJson(countryIso) {
    if (!countryIso) return [];

    const item = Array.from(
      this.element.querySelectorAll('.rs-country__selectbox .RevolutionSelectbox--Item')
    ).find(
      (el) =>
        (el.getAttribute('data-value') || '').toUpperCase() ===
        countryIso.toUpperCase()
    );
    const langs = this.parseLanguages(item?.getAttribute('data-languages') || '');
    if (langs.length) return langs;

    const data = await this.ensureLocalizationData();
    if (!data) return [];

    const match = (data.available_countries || []).find(
      (c) => c.iso_code?.toUpperCase() === countryIso.toUpperCase()
    );
    if (!match) return [];

    return (match.available_languages || [])
      .map((l) => ({
        iso: (l.iso_code || '').toLowerCase(),
        name: (l.endonym_name || l.name || l.iso_code || '').replace(
          /\b\w/g,
          (c) => c.toUpperCase()
        ),
      }))
      .filter((l) => l.iso);
  }

  rebuildLanguageList(langs) {
    const langBox = this.querySelector('.rs-language__selectbox');
    const hidden = this.querySelector('#language-selector__language-code');
    if (!langBox || !hidden || !langs.length) return;

    // Flat list markup: a scrollable `--InnerContent` of items, no toggle button/`--Content`.
    const inner = langBox.querySelector('.RevolutionSelectbox--InnerContent');
    if (!inner) return;

    const current = (hidden.value || '').toLowerCase();
    const hasCurrent = langs.some((l) => l.iso === current);
    const nextIso = langs.length === 1 ? langs[0].iso : (hasCurrent ? current : langs[0].iso);

    inner.textContent = '';

    langs.forEach((l) => {
      const item = document.createElement('div');
      item.className = 'RevolutionSelectbox--Item';
      // Roving tabindex: only the selected option is tabbable (see initRovingTabindex).
      item.tabIndex = l.iso === nextIso ? 0 : -1;
      item.setAttribute('role', 'option');
      item.setAttribute('data-value', l.iso);
      item.setAttribute('data-name', l.name);
      item.setAttribute('data-selectboxid', 'language-selector__language-code');
      item.setAttribute('data-action', 'selectbox-change');
      item.setAttribute('aria-selected', l.iso === nextIso ? 'true' : 'false');

      const title = document.createElement('div');
      title.className = 'RevolutionSelectbox--Title u-s3';
      title.textContent = l.name;
      item.appendChild(title);

      inner.appendChild(item);
    });

    hidden.value = nextIso;
  }


  async updateLanguageOptions(e) {
    // Only the language selector rebuilds; the market selector ignores its own broadcast.
    if (!this.querySelector('.rs-language__selectbox')) return;

    const raw = e?.detail?.languages || '';
    const countryIso = (e?.detail?.country || '').trim();

    // Re-stage the language form's country so a later language pick submits market + language.
    if (countryIso) {
      const countryHidden = this.querySelector('input[name="country_code"]');
      if (countryHidden) countryHidden.value = countryIso;
    }

    let langs = this.parseLanguages(raw);
    if (langs.length === 0) {
      langs = await this.fallbackLanguagesFromJson(countryIso);
    }
    if (langs.length === 0) return;

    this.rebuildLanguageList(langs);
  }
}
customElements.define('localization-modal', LocalizationModal);
